import logoWhite from '../../assets/logo_white_transparent.png';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDocTitle } from '../../hooks/useDocTitle';

/* ── Keyframes ─────────────────────────────────── */
const fadeUp   = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}`;
const gridScr  = keyframes`0%{background-position:0 0}100%{background-position:0 60px}`;
const popIn    = keyframes`0%{transform:scale(0) rotate(-8deg)}55%{transform:scale(1.15) rotate(2deg)}100%{transform:scale(1) rotate(0)}`;
const winGlow  = keyframes`0%,100%{opacity:1}50%{opacity:.45}`;
const drawLine = keyframes`from{clip-path:inset(0 100% 0 0)}to{clip-path:inset(0 0 0 0)}`;
const think    = keyframes`0%,100%{opacity:.3}50%{opacity:1}`;
const scanB    = keyframes`0%,100%{box-shadow:0 0 10px rgba(0,163,255,.25)}50%{box-shadow:0 0 30px rgba(0,163,255,.65)}`;



/* ── Layout ─────────────────────────────────────── */
const Page = styled.div`
  min-height:100vh;background:#02020E;display:flex;flex-direction:column;position:relative;overflow:hidden;
`;
const GridBg = styled.div`
  position:fixed;inset:0;
  background-image:linear-gradient(rgba(0,163,255,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,163,255,.03) 1px,transparent 1px);
  background-size:60px 60px;animation:${gridScr} 20s linear infinite;pointer-events:none;z-index:0;
`;

/* ── TopBar ─────────────────────────────────────── */
const TopBar = styled.header`
  position:relative;z-index:10;
  display:flex;align-items:center;justify-content:space-between;
  padding:15px 48px;border-bottom:1px solid rgba(0,163,255,.08);
  background:rgba(2,2,14,.94);backdrop-filter:blur(14px);
  @media(max-width:560px){padding:13px 20px}
`;
const BackBtn = styled(Link)`
  display:flex;align-items:center;gap:6px;
  font-family:'Space Mono',monospace;font-size:10px;letter-spacing:.22em;
  color:rgba(255,255,255,.4);text-decoration:none;text-transform:uppercase;transition:color .25s;
  span{font-size:14px}
  &:hover{color:#00A3FF}
`;
const LogoImg = styled(Link)`display:flex;align-items:center;img{height:30px;object-fit:contain}`;
const Chip = styled.span`
  font-family:'Space Mono',monospace;font-size:9px;letter-spacing:.28em;
  color:rgba(0,163,255,.5);border:1px solid rgba(0,163,255,.15);padding:4px 12px;text-transform:uppercase;
  @media(max-width:560px){display:none}
`;

/* ── Title ──────────────────────────────────────── */
const TitleArea = styled.div`
  text-align:center;padding:20px 20px 4px;position:relative;z-index:2;
  animation:${fadeUp} .55s ease both;
`;
const H1 = styled.h1`
  font-family:'Bebas Neue',sans-serif;
  font-size:clamp(44px,9vw,88px);line-height:.85;
  background:linear-gradient(135deg,#fff 30%,#00A3FF 100%);
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;letter-spacing:.04em;
`;

/* ── Config bar ─────────────────────────────────── */
const ConfigBar = styled.div`
  position:relative;z-index:2;
  display:flex;align-items:center;justify-content:center;gap:8px;
  padding:12px 24px 0;flex-wrap:wrap;
`;
const Chip2 = styled.button`
  font-family:'Space Mono',monospace;font-size:9px;letter-spacing:.2em;text-transform:uppercase;
  padding:6px 16px;
  border:1px solid ${p=>p.$a?'#00A3FF':'rgba(255,255,255,.1)'};
  background:${p=>p.$a?'rgba(0,163,255,.1)':'transparent'};
  color:${p=>p.$a?'#00A3FF':'rgba(255,255,255,.35)'};
  clip-path:polygon(6px 0%,100% 0%,calc(100% - 6px) 100%,0% 100%);
  transition:all .3s;
  &:hover{border-color:#00A3FF;color:#00A3FF}
`;
const Sep = styled.div`width:1px;height:20px;background:rgba(255,255,255,.08)`;

/* ── Scoreboard ─────────────────────────────────── */
const Scores = styled.div`
  position:relative;z-index:2;
  display:grid;grid-template-columns:1fr auto 1fr;
  align-items:center;gap:16px;
  max-width:520px;width:100%;margin:16px auto 0;padding:0 24px;
`;
const PCard = styled.div`
  background:${p=>p.$a?'rgba(0,163,255,.07)':'rgba(8,8,24,.6)'};
  border:1px solid ${p=>p.$a?'rgba(0,163,255,.3)':'rgba(255,255,255,.05)'};
  padding:12px 16px;text-align:${p=>p.$r?'right':'left'};transition:all .3s;
  ${p=>p.$a && css`animation:${scanB} 2s ease infinite`};
`;
const PName = styled.div`
  font-family:'Space Mono',monospace;font-size:9px;letter-spacing:.22em;
  color:rgba(255,255,255,.3);text-transform:uppercase;margin-bottom:4px;
  display:flex;align-items:center;gap:5px;
  ${p=>p.$r&&'justify-content:flex-end'};
`;
const Dot = styled.span`
  width:5px;height:5px;border-radius:50%;background:#00A3FF;
  display:inline-block;animation:${think} 1.2s ease infinite;
`;
const PScore = styled.div`
  font-family:'Bebas Neue',sans-serif;font-size:42px;line-height:1;
  background:${p=>p.$g};-webkit-background-clip:text;-webkit-text-fill-color:transparent;
`;
const VSBox = styled.div`text-align:center`;
const VS = styled.div`font-family:'Bebas Neue',sans-serif;font-size:26px;color:rgba(255,255,255,.12)`;
const Draws = styled.div`font-family:'Space Mono',monospace;font-size:9px;color:rgba(255,255,255,.25);letter-spacing:.15em`;

/* ── AI Status ──────────────────────────────────── */
const Status = styled.div`
  position:relative;z-index:2;
  min-height:30px;text-align:center;padding:10px 24px 0;
  font-family:'Space Mono',monospace;font-size:10px;
  letter-spacing:.22em;color:rgba(0,163,255,.5);text-transform:uppercase;
`;
const ThinkDots = styled.span`
  display:inline-flex;gap:4px;margin-left:6px;
  span{width:4px;height:4px;border-radius:50%;background:#00A3FF;display:inline-block;
    animation:${think} 1s ease infinite;
    &:nth-child(2){animation-delay:.2s}&:nth-child(3){animation-delay:.4s}
  }
`;

/* ── Board ──────────────────────────────────────── */
const BoardWrap = styled.div`
  position:relative;z-index:2;
  display:flex;justify-content:center;align-items:center;
  padding:16px 24px 8px;flex:1;
`;
const Board = styled.div`
  display:grid;grid-template-columns:repeat(3,1fr);gap:6px;
  width:100%;max-width:380px;position:relative;
`;
const Cell = styled.button`
  aspect-ratio:1;
  background:${p=>p.$win?'rgba(0,163,255,.1)':p.$hot?'rgba(0,163,255,.045)':'rgba(8,8,24,.6)'};
  border:1px solid ${p=>p.$win?'rgba(0,163,255,.4)':'rgba(255,255,255,.06)'};
  display:flex;align-items:center;justify-content:center;
  font-size:clamp(26px,8vw,58px);
  transition:all .22s;position:relative;overflow:hidden;
  ${p=>p.$win && css`animation:${winGlow} 1.2s ease infinite`};
  &:hover:not(:disabled){border-color:rgba(0,163,255,.2);background:rgba(0,163,255,.06)}
  &:disabled{opacity:1}
`;
const Sym = styled.div`
  font-family:'Orbitron',monospace;font-weight:900;font-size:inherit;
  color:${p=>p.$s==='X'?'#00A3FF':'#C800FF'};
  text-shadow:${p=>p.$s==='X'?'0 0 18px rgba(0,163,255,.7)':'0 0 18px rgba(200,0,255,.7)'};
  line-height:1;animation:${popIn} .22s cubic-bezier(.4,0,.2,1) both;
`;
const Ghost = styled.div`
  font-family:'Orbitron',monospace;font-weight:900;font-size:inherit;
  color:${p=>p.$s==='X'?'#00A3FF':'#C800FF'};opacity:.18;line-height:1;
`;

/* Win line */
const WinLine = styled.div`
  position:absolute;border-radius:3px;z-index:5;
  background:linear-gradient(90deg,#00A3FF,#C800FF);
  box-shadow:0 0 18px rgba(0,163,255,.5);
  animation:${drawLine} .38s ease forwards;
  ${p=>({
    row0:`height:4px;width:calc(100% - 12px);left:6px;top:calc(33.33% - 2px)`,
    row1:`height:4px;width:calc(100% - 12px);left:6px;top:calc(50% - 2px)`,
    row2:`height:4px;width:calc(100% - 12px);left:6px;top:calc(66.66% - 2px)`,
    col0:`width:4px;height:calc(100% - 12px);top:6px;left:calc(16.66% - 2px)`,
    col1:`width:4px;height:calc(100% - 12px);top:6px;left:calc(50% - 2px)`,
    col2:`width:4px;height:calc(100% - 12px);top:6px;left:calc(83.33% - 2px)`,
    diag0:`height:4px;width:138%;left:-19%;top:50%;transform-origin:center;transform:translateY(-50%) rotate(45deg)`,
    diag1:`height:4px;width:138%;left:-19%;top:50%;transform-origin:center;transform:translateY(-50%) rotate(-45deg)`,
  }[p.$d]||'')}
`;

/* ── Buttons ─────────────────────────────────────── */
const BtnRow = styled.div`
  position:relative;z-index:2;
  display:flex;justify-content:center;gap:10px;padding:16px 24px 36px;flex-wrap:wrap;
`;
const Btn = styled.button`
  font-family:'Rajdhani',sans-serif;font-weight:700;font-size:12px;
  letter-spacing:.2em;text-transform:uppercase;padding:10px 26px;
  background:${p=>p.$p?'linear-gradient(135deg,#00A3FF,#6B00FF)':'transparent'};
  border:1px solid ${p=>p.$p?'transparent':'rgba(255,255,255,.12)'};
  color:${p=>p.$p?'#fff':'rgba(255,255,255,.4)'};
  clip-path:polygon(8px 0%,100% 0%,calc(100% - 8px) 100%,0% 100%);
  transition:all .3s;
  &:hover{box-shadow:0 0 18px rgba(0,163,255,.3);transform:translateY(-2px)}
`;

/* ── Overlay / Modal ────────────────────────────── */
const Overlay = styled.div`
  position:fixed;inset:0;z-index:100;
  background:rgba(2,2,14,.92);backdrop-filter:blur(8px);
  display:flex;align-items:center;justify-content:center;
`;
const Modal = styled.div`
  text-align:center;padding:52px 44px;
  border:1px solid rgba(0,163,255,.2);background:rgba(6,5,20,.97);
  max-width:380px;width:90%;position:relative;overflow:hidden;
  animation:${fadeUp} .4s ease;
  &::before{content:'';position:absolute;top:0;left:0;width:3px;height:100%;background:linear-gradient(to bottom,#00A3FF,#C800FF)}
`;
const MEmo = styled.div`font-size:60px;margin-bottom:12px`;
const MTit = styled.h2`font-family:'Bebas Neue',sans-serif;font-size:50px;letter-spacing:.04em;color:#fff;margin-bottom:8px`;
const MSub = styled.p`font-size:14px;color:rgba(255,255,255,.4);margin-bottom:20px;line-height:1.6`;
const MBtns = styled.div`display:flex;gap:8px;justify-content:center;flex-wrap:wrap`;
const MBtn  = styled.button`
  font-family:'Rajdhani',sans-serif;font-weight:700;font-size:12px;
  letter-spacing:.2em;text-transform:uppercase;padding:11px 26px;
  background:${p=>p.$p?'linear-gradient(135deg,#00A3FF,#6B00FF)':'transparent'};
  border:1px solid ${p=>p.$p?'transparent':'rgba(255,255,255,.15)'};
  color:${p=>p.$p?'#fff':'rgba(255,255,255,.45)'};
  clip-path:polygon(8px 0%,100% 0%,calc(100% - 8px) 100%,0% 100%);
  transition:all .3s;
  &:hover{box-shadow:0 0 18px rgba(0,163,255,.3)}
`;

/* ── Game Logic ─────────────────────────────────── */
const WINS = [
  {c:[0,1,2],d:'row0'},{c:[3,4,5],d:'row1'},{c:[6,7,8],d:'row2'},
  {c:[0,3,6],d:'col0'},{c:[1,4,7],d:'col1'},{c:[2,5,8],d:'col2'},
  {c:[0,4,8],d:'diag0'},{c:[2,4,6],d:'diag1'},
];

function checkResult(b){
  for(const{c,d}of WINS){
    if(b[c[0]]&&b[c[0]]===b[c[1]]&&b[c[0]]===b[c[2]])
      return{winner:b[c[0]],dir:d,combo:c};
  }
  if(b.every(Boolean))return{winner:'draw'};
  return null;
}

function minimax(b,isMax,depth,alpha,beta){
  const r=checkResult(b);
  if(r){
    if(r.winner==='O')return 10-depth;
    if(r.winner==='X')return depth-10;
    return 0;
  }
  if(isMax){
    let best=-Infinity;
    for(let i=0;i<9;i++){
      if(!b[i]){b[i]='O';best=Math.max(best,minimax(b,false,depth+1,alpha,beta));b[i]='';alpha=Math.max(alpha,best);if(beta<=alpha)break;}
    }
    return best;
  }else{
    let best=Infinity;
    for(let i=0;i<9;i++){
      if(!b[i]){b[i]='X';best=Math.min(best,minimax(b,true,depth+1,alpha,beta));b[i]='';beta=Math.min(beta,best);if(beta<=alpha)break;}
    }
    return best;
  }
}

function aiMove(b,diff){
  const empty=b.map((v,i)=>(!v?i:null)).filter(v=>v!==null);
  if(!empty.length)return null;
  if(diff==='fácil'&&Math.random()<.65)return empty[Math.floor(Math.random()*empty.length)];
  if(diff==='médio'&&Math.random()<.3) return empty[Math.floor(Math.random()*empty.length)];
  let best=-Infinity,move=empty[0];
  for(const i of empty){
    b[i]='O';const s=minimax(b,false,0,-Infinity,Infinity);b[i]='';
    if(s>best){best=s;move=i}
  }
  return move;
}

/* ── Component ──────────────────────────────────── */
export default function TicTacToe(){
  const { t } = useTranslation();
  useDocTitle('pageTitles.tictactoe');
  const [mode,   setMode]   = useState('vs-ai');
  const [diff,   setDiff]   = useState('médio');
  const [board,  setBoard]  = useState(Array(9).fill(''));
  const [turn,   setTurn]   = useState('X');
  const [result, setResult] = useState(null);
  const [scores, setScores] = useState({X:0,O:0,D:0});
  const [thinking,setThinking]=useState(false);
  const [modal,  setModal]  = useState(false);
  const [hovered,setHovered]=useState(null);

  const aiTimer = useRef(null);

  const reset = useCallback(()=>{
    clearTimeout(aiTimer.current);
    setBoard(Array(9).fill(''));
    setTurn('X');setResult(null);setThinking(false);setModal(false);
  },[]);

  /* AI turn */
  useEffect(()=>{
    if(mode!=='vs-ai'||turn!=='O'||result)return;
    setThinking(true);
    const delay = diff==='fácil'?380:diff==='médio'?580:820;
    aiTimer.current = setTimeout(()=>{
      setBoard(prev=>{
        const b=[...prev];
        const m=aiMove(b,diff);
        if(m==null){setThinking(false);return prev;}
        b[m]='O';
        const res=checkResult(b);
        if(res){
          setResult(res);
          setScores(sc=>({...sc,[res.winner==='draw'?'D':res.winner]:sc[res.winner==='draw'?'D':res.winner]+1}));
          setTimeout(()=>setModal(true),500);
        }else{
          setTurn('X');
        }
        setThinking(false);
        return b;
      });
    },delay);
    return()=>clearTimeout(aiTimer.current);
  },[turn,mode,result,diff]);

  const handleCell = useCallback((i)=>{
    if(result||board[i]||thinking)return;
    if(mode==='vs-ai'&&turn!=='X')return;
    const b=[...board];b[i]=turn;
    setBoard(b);
    const res=checkResult(b);
    if(res){
      setResult(res);
      setScores(sc=>({...sc,[res.winner==='draw'?'D':res.winner]:sc[res.winner==='draw'?'D':res.winner]+1}));
      setTimeout(()=>setModal(true),500);
    }else{
      setTurn(t=>t==='X'?'O':'X');
    }
  },[result,board,thinking,mode,turn]);

  const humanTurn=(mode==='2p')||(mode==='vs-ai'&&turn==='X');
  const p1=mode==='2p' ? t('games.tictactoe.player1') : t('games.tictactoe.you');
  const p2=mode==='2p' ? t('games.tictactoe.player2') : t('games.tictactoe.aiLabel', { diff: diff.toUpperCase() });

  const modalInfo=()=>{
    if(!result)return{e:'',title:'',sub:''};
    if(result.winner==='draw')return{e:'🤝',title: t('games.tictactoe.modalDraw'),sub: t('games.tictactoe.modalDrawSub')};
    if(mode==='vs-ai'){
      return result.winner==='X'
        ?{e:'🏆',title: t('games.tictactoe.modalWin'),sub: t('games.tictactoe.modalWinSub')}
        :{e:'🤖',title: t('games.tictactoe.modalAiWin'),sub: t('games.tictactoe.modalAiWinSub', { diff })};
    }
    return{e:'🏆',title: result.winner==='X' ? t('games.tictactoe.modalP1Win') : t('games.tictactoe.modalP2Win'),sub: t('games.tictactoe.modalGoodGame')};
  };
  const{e,title,sub}=modalInfo();

  return(
    <Page>
      <GridBg/>
      <TopBar>
        <BackBtn to="/games"><span>←</span>{t('games.backToGames')}</BackBtn>
        <LogoImg to="/"><img src={logoWhite} alt="Guebly Games" style={{height:"32px",objectFit:"contain",display:"block"}}/></LogoImg>
        <Chip>Tic Tac Toe</Chip>
      </TopBar>

      <TitleArea><H1>TIC TAC TOE</H1></TitleArea>

      <ConfigBar>
        <Chip2 $a={mode==='vs-ai'} onClick={()=>{setMode('vs-ai');reset()}}>{t('games.tictactoe.vsAi')}</Chip2>
        <Chip2 $a={mode==='2p'}    onClick={()=>{setMode('2p');reset()}}>{t('games.tictactoe.vs2p')}</Chip2>
        {mode==='vs-ai'&&<>
          <Sep/>
          {['fácil','médio','difícil'].map(d=>(
            <Chip2 key={d} $a={diff===d} onClick={()=>{setDiff(d);reset()}}>
              {d === 'fácil' ? t('games.memory.diffEasy') : d === 'médio' ? t('games.memory.diffMedium') : t('games.memory.diffHard')}
            </Chip2>
          ))}
        </>}
      </ConfigBar>

      <Scores>
        <PCard $a={turn==='X'&&!result}>
          <PName>{p1}{turn==='X'&&!result&&<Dot/>}</PName>
          <PScore $g="linear-gradient(135deg,#00A3FF,#6B00FF)">{scores.X}</PScore>
        </PCard>
        <VSBox><VS>VS</VS><Draws>{t('games.tictactoe.draws', { count: scores.D })}</Draws></VSBox>
        <PCard $a={turn==='O'&&!result} $r>
          <PName $r>{turn==='O'&&!result&&<Dot/>}{p2}</PName>
          <PScore $g="linear-gradient(135deg,#C800FF,#6B00FF)">{scores.O}</PScore>
        </PCard>
      </Scores>

      <Status>
        {thinking&&<>{t('games.tictactoe.thinking')}<ThinkDots><span/><span/><span/></ThinkDots></>}
        {!thinking&&!result&&(
          mode==='2p'
            ?(turn==='X' ? t('games.tictactoe.turnPlayer1X') : t('games.tictactoe.turnPlayer2O'))
            :(turn==='X' ? t('games.tictactoe.turnYourX') : t('games.tictactoe.aiThinking'))
        )}
        {result&&result.winner==='draw'&&t('games.tictactoe.draw')}
        {result&&result.winner!=='draw'&&(
          mode==='vs-ai'
            ?(result.winner==='X' ? t('games.tictactoe.youWon') : t('games.tictactoe.aiWon'))
            :(t('games.tictactoe.playerWon', { player: result.winner==='X' ? t('games.tictactoe.player1') : t('games.tictactoe.player2') }))
        )}
      </Status>

      <BoardWrap>
        <Board>
          {result&&result.winner!=='draw'&&<WinLine $d={result.dir}/>}
          {board.map((cell,i)=>(
            <Cell
              key={i}
              $win={result?.combo?.includes(i)}
              $hot={!cell&&!result&&!thinking&&hovered===i&&humanTurn}
              disabled={!!cell||!!result||thinking||(mode==='vs-ai'&&turn==='O')}
              onClick={()=>handleCell(i)}
              onMouseEnter={()=>setHovered(i)}
              onMouseLeave={()=>setHovered(null)}
            >
              {cell&&<Sym $s={cell}>{cell}</Sym>}
              {!cell&&!result&&humanTurn&&hovered===i&&<Ghost $s={turn}>{turn}</Ghost>}
            </Cell>
          ))}
        </Board>
      </BoardWrap>

      <BtnRow>
        <Btn $p onClick={reset}>{t('games.tictactoe.btnNewGame')}</Btn>
        <Btn onClick={()=>setScores({X:0,O:0,D:0})}>{t('games.tictactoe.btnResetScore')}</Btn>
      </BtnRow>

      {modal&&(
        <Overlay>
          <Modal>
            <MEmo>{e}</MEmo>
            <MTit>{title}</MTit>
            <MSub>{sub}</MSub>
            <MBtns>
              <MBtn $p onClick={reset}>{t('games.tictactoe.modalPlayAgain')}</MBtn>
              <MBtn onClick={()=>setModal(false)}>{t('games.tictactoe.modalViewBoard')}</MBtn>
            </MBtns>
          </Modal>
        </Overlay>
      )}
    </Page>
  );
}
